import { type FC } from 'react';
import type { ToastOptions } from 'react-toastify';

import { errorToast } from '@/helpers';
import { _isBlank, _isEmpty, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import { type ApiErrorFieldDetail, parseApiErrorResponse } from '@/utils/apiErrorResponse';

import styles from './ApiErrorToast.module.scss';

/** Beyond this many lines the toast summarises the remainder rather than growing without bound. */
const MAX_DETAIL_LINES = 10;

const DEFAULT_AUTO_CLOSE_MS = 10_000;

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Removes the raw payload path from the front of a validation message. The API renders messages
 * with the attribute embedded ("The treatment_types.0.name has already been taken"), which would
 * otherwise repeat — in its least readable form — the label already shown beside it.
 */
export const stripFieldPathPrefix = (message: string, path: string): string => {
  if (_isBlank(path)) {
    return message;
  }

  const stripped = message.replace(new RegExp(`^\\s*(the\\s+)?${escapeForRegExp(path)}\\s*`, 'i'), '');
  return _isNotBlank(stripped) ? stripped : message;
};

/** Renders a field path segment as a sentence-cased label, e.g. `display_name` to `Display name`. */
const humaniseField = (field: string): string => {
  const words = field.replace(/[._]/g, ' ').trim();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

/**
 * Names the thing a message is about, preferring whatever the caller can resolve from its own
 * payload. Falls back to the row position, then the field name, and finally to no label at all —
 * a bare message reads better than an opaque payload path.
 */
const resolveLabel = (
  detail: ApiErrorFieldDetail,
  labelForDetail?: (detail: ApiErrorFieldDetail) => string | undefined
): string | undefined => {
  const provided = labelForDetail?.(detail);
  if (_notNil(provided) && _isNotBlank(provided)) {
    return provided;
  }

  if (_notNil(detail.index)) {
    return `Row ${detail.index + 1}`;
  }

  return _notNil(detail.field) && _isNotBlank(detail.field) ? humaniseField(detail.field) : undefined;
};

interface DetailLine {
  key: string;
  label?: string;
  message: string;
}

const buildDetailLines = (
  details: Array<ApiErrorFieldDetail>,
  labelForDetail?: (detail: ApiErrorFieldDetail) => string | undefined
): Array<DetailLine> =>
  details.reduce<Array<DetailLine>>((lines, detail) => {
    const label = resolveLabel(detail, labelForDetail);
    detail.messages.forEach((message, messageIndex) => {
      lines.push({
        key: `${detail.path}-${messageIndex}`,
        label,
        message: stripFieldPathPrefix(message, detail.path),
      });
    });
    return lines;
  }, []);

interface ApiErrorToastContentProps {
  title: string;
  summary?: string;
  lines: Array<DetailLine>;
  testId?: string;
}

/**
 * Body of an API error toast: the caller's own headline, the server's summary of what went
 * wrong, and one line per field-level message so a rejected bulk operation names the entries
 * the server objected to.
 */
export const ApiErrorToastContent: FC<ApiErrorToastContentProps> = ({
  title,
  summary,
  lines,
  testId = 'api-error-toast',
}) => {
  const visibleLines = lines.slice(0, MAX_DETAIL_LINES);
  const hiddenCount = lines.length - visibleLines.length;

  return (
    <div data-testid={testId}>
      <p className="fw6 ma0" data-testid={`${testId}__title`}>
        {title}
      </p>
      {_notNil(summary) && (
        <p className="ma0 f6 lh-copy" data-testid={`${testId}__summary`}>
          {summary}
        </p>
      )}
      {_isNotEmpty(visibleLines) && (
        <ul className={`f6 lh-copy ${styles.details}`} data-testid={`${testId}__details`}>
          {visibleLines.map(({ key, label, message }) => (
            <li key={key} className={styles.detail}>
              {_notNil(label) ? (
                <>
                  <span className="fw6">{label}</span>
                  {` — ${message}`}
                </>
              ) : (
                message
              )}
            </li>
          ))}
        </ul>
      )}
      {hiddenCount > 0 && (
        <p className={`f6 ${styles.overflow}`} data-testid={`${testId}__overflow`}>
          {`…and ${hiddenCount} more`}
        </p>
      )}
    </div>
  );
};

export interface ApiErrorToastParams {
  /** Headline shown regardless of what detail the response carried. */
  title: string;
  /** Anything a request rejected or resolved with: an axios error, an `InVivoError`, or an `ApiErrorResponse`. */
  error: unknown;
  /**
   * Names the entry a field-level message refers to. The API reports paths into the request
   * payload, so only the caller can map an index back to what the user actually entered.
   * Returning `undefined` falls back to a generic label.
   */
  labelForDetail?: (detail: ApiErrorFieldDetail) => string | undefined;
  options?: ToastOptions;
  testId?: string;
}

/**
 * Surfaces the rich error body the API returns — `{ message, errors }` — as a multi-line error
 * toast, so a researcher learns which entry was rejected and why rather than only that the
 * request failed.
 *
 * Degrades to a plain `title` toast when the response carries no usable detail, which is the
 * case for network failures and unhandled server errors.
 */
export const apiErrorToast = ({ title, error, labelForDetail, options, testId }: ApiErrorToastParams): void => {
  const { summary, details } = parseApiErrorResponse(error);
  const lines = buildDetailLines(details, labelForDetail);

  if (_isNil(summary) && _isEmpty(lines)) {
    errorToast(title, options);
    return;
  }

  errorToast(<ApiErrorToastContent title={title} summary={summary} lines={lines} testId={testId} />, {
    autoClose: DEFAULT_AUTO_CLOSE_MS,
    ...(options ?? {}),
  });
};
